import { defineStore } from 'pinia';
import { decryptResponse } from '@/services/crypto.js';
import { firebaseAuth } from '@/services/firebase.js';

// we’ll keep using static JSON for now

// Placeholder for static tracks (will be replaced by API fetch)
let coreTrack = { modules: [] };
let intensiveTrack = { modules: [] };
let masteryTrack = { modules: [] };

// --- TRANSFORM API RESPONSE TO CURRICULUM FORMAT ---
function transformApiToCoreTrack(apiObj) {
      // apiObj: EPLUS_CORE object from API
      // Returns: { trackId, name, description, modules: [...] }
      const modules = (apiObj.modules || []).map(mod => ({
        id: mod.name || mod._id || mod.id,
        title: mod.title,
        order: mod.rownum || 0,
        units: (mod.units || []).map(unit => ({
          id: unit.name || unit._id || unit.id,
          title: unit.title,
          order: unit.rownum || 0,
          lessons: (unit.lessons || []).map(lesson => ({
            id: lesson.name || lesson._id || lesson.id,
            title: lesson.title,
            // 1–5 competency level: used after diagnostic to auto-seed lessons the
            // student is clearly above (see useDiagnosticSession commitResults).
            level: lesson.level || null,
            // Plan-gating: "NONE"|"FREE"|"PRO"|"FAMILY" — see usePlanGating.js
            excludedPlans: lesson.excludedPlans || 'NONE',
            // Map subsections if present, else fallback to content as a single subsection
            subsections: Array.isArray(lesson.subsections) && lesson.subsections.length > 0
              ? lesson.subsections.map(sub => ({
                rownum: sub.rownum || 0,
                type: sub.type || 'FORMATSECTIONGROUP',
                content: sub.content || '',
                title: sub.title || '',
                level: sub.level || null
              }))
              : [],
            // Optionally map learningContent if present
            learningContent: lesson.learningContent || undefined
          }))
        }))
      }));
      return {
        trackId: apiObj.slug || apiObj.name || 'core',
        name: apiObj.title || apiObj.name,
        description: apiObj.desc || apiObj.description,
        modules
      };
    }

// Cache duration in milliseconds (30 minutes)
const CACHE_DURATION = 30 * 60 * 1000;
const CACHE_KEY = 'curriculum_cache';

function getCache() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('Failed to cache curriculum:', e);
  }
}

export const useCurriculumStore = defineStore('curriculumStore', {
  state: () => ({
    trackId: null,
    modules: [],
    isLoaded: false,
    isLoading: false,
    lastLoadedAt: null
  }),

  getters: {
    // Flatten all lessons
    allLessons(state) {
      return state.modules.flatMap(mod =>
        mod.units.flatMap(unit =>
          unit.lessons.map(lesson => ({
            ...lesson,
            moduleId: mod.id,
            moduleTitle: mod.title,
            unitId: unit.id,
            unitTitle: unit.title
          }))
        )
      );
    },

    // Flatten all section groups with context
    allSectionGroups(state) {
      const out = [];
      for (const mod of state.modules) {
        for (const unit of mod.units) {
          for (const lesson of unit.lessons) {
            for (const subsection of lesson.subsections || []) {
              if (!subsection.content) continue;
              out.push({
                sectionGroupId: subsection.content,
                sectionGroupTitle: subsection.title,
                lessonId: lesson.id,
                lessonTitle: lesson.title,
                excludedPlans: lesson.excludedPlans || 'NONE',
                unitId: unit.id,
                unitTitle: unit.title,
                moduleId: mod.id,
                moduleTitle: mod.title
              });
            }
          }
        }
      }
      return out;
    },

    getModulesForTrack: () => (trackId) => {
      if (trackId === 'core') return coreTrack.modules;
      // uncomment later when files are ready
      // if (trackId === 'intensive') return intensiveTrack.modules;
      // if (trackId === 'mastery') return masteryTrack.modules;

      console.warn("Unknown track in getModulesForTrack:", trackId);
      return coreTrack.modules;
    }
  },

  actions: {
    async loadTrack(trackId = 'core') {
      // Already loaded for this track - skip
      if (this.isLoaded && this.trackId === trackId && this.modules.length > 0) {
        return;
      }

      // Already loading - wait for it
      if (this.isLoading) {
        return;
      }

      // Try to load from session cache first
      const cached = getCache();
      if (cached && cached.trackId === trackId) {
        this.trackId = cached.trackId;
        this.modules = cached.modules;
        this.isLoaded = true;
        this.lastLoadedAt = cached.loadedAt;
        console.log('Curriculum loaded from cache');
        return;
      }

      // Fetch from API
      this.isLoading = true;

      const apiBase = import.meta.env.VITE_API_BASE;
      const apiUrl = `${apiBase}/curriculum`;

      try {
        const user = firebaseAuth.currentUser;
        const token = user ? await user.getIdToken() : null;
        const response = await fetch(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error('Failed to fetch curriculum');

        const apiData = await response.json().then(decryptResponse);
        const eplusCore = apiData?.response?.[0]?.paginatedResults?.find(t => t.name === 'EPLUS_CORE');

        if (eplusCore) {
          coreTrack = transformApiToCoreTrack(eplusCore);
        }
      } catch (e) {
        console.warn('Failed to fetch curriculum from API, using static fallback', e);
      }

      // Select track data
      let data = null;
      if (trackId === 'core') data = coreTrack;
      else if (trackId === 'intensive') data = intensiveTrack;
      else if (trackId === 'mastery') data = masteryTrack;

      if (!data || !data.modules?.length) {
        console.warn('Unknown trackId or empty data, defaulting to core');
        data = coreTrack;
      }

      // Update store state
      this.trackId = data.trackId || trackId;
      this.modules = data.modules || [];
      this.isLoaded = true;
      this.isLoading = false;
      this.lastLoadedAt = Date.now();

      // Cache the result
      setCache({
        trackId: this.trackId,
        modules: this.modules,
        loadedAt: this.lastLoadedAt
      });

      console.log('Curriculum loaded from API and cached');
    },

    // Force reload from API (bypasses cache)
    async reloadTrack(trackId = 'core') {
      sessionStorage.removeItem(CACHE_KEY);
      this.isLoaded = false;
      await this.loadTrack(trackId);
    },

    // Clear cache
    clearCache() {
      sessionStorage.removeItem(CACHE_KEY);
      this.isLoaded = false;
    }
  }
});
